/**
 * Historial de cambios sobre usuarios (Users → Historial y el historial de
 * cada usuario): cómo se LEE cada fila que devuelve GET /api/backoffice/audit.
 *
 * El Worker guarda los campos que cambian ({ field, from?, to? }) y unas
 * etiquetas; aquí se traducen a frases ("Dio licencia: Premium prueba hasta
 * el 5/11/2026") para que no haga falta leer campo a campo. Lógica pura: sin
 * React ni red (test en tests/backoffice-audit-log-test.mjs).
 */

// Filtros del buscador: [etiqueta del Worker, texto].
export const AUDIT_FILTERS = [
  ['all', 'Todo'],
  ['license', 'Licencias'],
  ['license_grant', 'Licencia dada'],
  ['license_revoke', 'Licencia quitada'],
  ['role', 'Roles'],
  ['founder', 'Founder'],
  ['ambassador', 'Embajador'],
  ['account', 'Cuenta'],
  ['delete', 'Eliminados']
];

const PLAN_LABELS = {
  free: 'Free',
  premium_internal: 'Premium interno',
  premium_ambassador: 'Premium embajador',
  premium_marketing: 'Premium Marketing',
  premium_trial: 'Premium prueba',
  founders_premium: 'Founder Premium',
  premium_monthly: 'Premium mensual',
  premium_3months: 'Premium 3 meses',
  premium_year: 'Premium anual',
  performance_monthly: 'Performance mensual',
  performance_3months: 'Performance 3 meses',
  performance_year: 'Performance anual'
};

export function planLabel(plan) {
  if (plan == null || plan === '') return 'Free';
  return PLAN_LABELS[plan] || String(plan).replace(/_/g, ' ');
}

const isTimestamp = (v) => !!v && typeof v === 'object' && v.__type === 'timestamp';

export function auditDate(value, { time = false } = {}) {
  const ms = isTimestamp(value) ? Date.parse(value.value) : typeof value === 'number' ? value : Date.parse(value);
  if (!Number.isFinite(ms)) return '—';
  const d = new Date(ms);
  return time
    ? d.toLocaleString('es-ES', { day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('es-ES', { day: 'numeric', month: 'numeric', year: 'numeric' });
}

/** Valor de un campo tal cual se enseña en el detalle. */
export function auditValue(value) {
  if (value === undefined) return '—';
  if (value === null) return 'vacío';
  if (isTimestamp(value)) return auditDate(value, { time: true });
  if (value === true) return 'sí';
  if (value === false) return 'no';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value) === '' ? '(texto vacío)' : String(value);
}

const has = (change, key) => Object.prototype.hasOwnProperty.call(change, key);
const isOn = (change) => has(change, 'to') && change.to === true;
const byField = (changes) => new Map((changes || []).map((c) => [c.field, c]));

// Campos con datos personales: el rol viewer los ve enmascarados.
const EMAIL_FIELD = /email/i;
const HANDLE_FIELD = /^(tiktokUsername|tiktokLinkedUsername|displayName)$/;
export function sensitiveKind(field) {
  const top = String(field || '').split('.')[0];
  if (EMAIL_FIELD.test(top)) return 'email';
  if (HANDLE_FIELD.test(top)) return 'handle';
  return null;
}

function licenseLines(item, fields) {
  const plan = fields.get('plan');
  const end = fields.get('planEnd');
  const lines = [];
  const until = end && has(end, 'to') && end.to != null ? ` hasta el ${auditDate(end.to)}` : '';
  if (item.tags.includes('license_grant')) {
    lines.push({ tone: 'success', text: `Dio licencia: ${planLabel(plan?.to)}${until}` });
  } else if (item.tags.includes('license_revoke')) {
    lines.push({ tone: 'danger', text: `Quitó la licencia (tenía ${planLabel(plan?.from)})` });
  } else if (plan) {
    lines.push({ tone: 'warning', text: `Cambió el plan: ${planLabel(plan.from)} → ${planLabel(plan.to)}${until}` });
  } else if (end) {
    const hadEnd = has(end, 'from') && end.from != null;
    const hasEnd = has(end, 'to') && end.to != null;
    if (hadEnd && hasEnd) lines.push({ tone: 'warning', text: `Cambió el fin del plan: ${auditDate(end.from)} → ${auditDate(end.to)}` });
    else if (hasEnd) lines.push({ tone: 'warning', text: `Puso fin del plan: ${auditDate(end.to)}` });
    else lines.push({ tone: 'warning', text: `Quitó el fin del plan (era ${auditDate(end.from)})` });
  }
  const agency = fields.get('agencyLicense');
  if (agency) {
    const hasIt = has(agency, 'to') && agency.to != null && agency.to !== '' && agency.to !== false;
    lines.push(hasIt
      ? { tone: 'success', text: `Puso licencia de agencia: ${auditValue(agency.to)}` }
      : { tone: 'danger', text: `Quitó la licencia de agencia (${auditValue(agency.from)})` });
  }
  if ([...fields.keys()].some((f) => /^frozen/.test(f))) lines.push({ tone: 'default', text: 'Tocó el plan congelado' });
  if (!lines.length) lines.push({ tone: 'warning', text: 'Cambió datos de la licencia' });
  return lines;
}

function roleLines(fields) {
  const lines = [];
  for (const [field, change] of fields) {
    const [top, sub] = field.split('.');
    if (top === 'ADMIN') {
      lines.push(isOn(change) ? { tone: 'warning', text: 'Hizo ADMIN' } : { tone: 'danger', text: 'Quitó ADMIN' });
    } else if (top === 'ADMIN_VIEWER') {
      lines.push(isOn(change)
        ? { tone: 'warning', text: 'Dio acceso de solo lectura al backoffice' }
        : { tone: 'danger', text: 'Quitó el acceso de solo lectura al backoffice' });
    } else if (top === 'agencyRoles' && !sub) {
      lines.push({ tone: 'default', text: 'Cambió los roles de agencia' });
    } else if (top === 'agencyRoles') {
      const role = has(change, 'to') && typeof change.to === 'string' ? change.to : '';
      const name = role === 'owner' ? 'Owner' : role === 'manager' ? 'Manager' : role;
      lines.push(role
        ? { tone: 'warning', text: `Hizo ${name} de la agencia ${sub || ''}`.trim() }
        : { tone: 'danger', text: `Quitó el rol de ${change.from === 'owner' ? 'Owner' : change.from === 'manager' ? 'Manager' : 'agencia'} en ${sub || 'la agencia'}` });
    } else if (/^(OWNER|MANAGER)_/.test(top)) {
      lines.push({ tone: 'default', text: `${isOn(change) ? 'Puso' : 'Quitó'} la marca antigua ${top}` });
    } else if (top === 'role' || top === 'roles') {
      lines.push({ tone: 'default', text: `Cambió ${top}` });
    }
  }
  return lines;
}

/**
 * Fila del historial → frases, la más importante primero.
 * → [{ text, tone: 'success' | 'danger' | 'warning' | 'default' }]
 */
export function describeAudit(item) {
  const tags = item?.tags || [];
  const changes = item?.changes || [];
  const fields = byField(changes);
  if (item?.action === 'user.delete') {
    const plan = fields.get('plan');
    const founder = fields.get('founder.number');
    const extra = [plan && `tenía ${planLabel(plan.from)}`, founder && `Founder n.º ${founder.from}`].filter(Boolean).join(' · ');
    return [{ tone: 'danger', text: `Eliminó la cuenta${extra ? ` (${extra})` : ''}` }];
  }
  if (item?.action === 'user.roblox_unlink') {
    const roblox = fields.get('roblox');
    return [{ tone: 'warning', text: `Desvinculó Roblox${roblox?.from ? ` (id ${roblox.from})` : ''}` }];
  }
  const lines = [];
  if (tags.includes('license')) lines.push(...licenseLines(item, fields));
  if (tags.includes('role')) lines.push(...roleLines(fields));
  if (tags.includes('founder')) {
    const number = fields.get('founder.number');
    const active = fields.get('founder.active');
    if (number && !has(number, 'from')) lines.push({ tone: 'success', text: `Hizo Founder n.º ${number.to}` });
    else if (number && !has(number, 'to')) lines.push({ tone: 'danger', text: `Quitó Founder (era el n.º ${number.from})` });
    else if (active && has(active, 'to')) lines.push({ tone: active.to ? 'success' : 'danger', text: active.to ? 'Activó Founder' : 'Desactivó Founder' });
    else lines.push({ tone: 'default', text: 'Cambió datos de Founder' });
  }
  if (tags.includes('ambassador')) {
    const active = fields.get('ambassador.active');
    const all = changes.filter((c) => c.field.startsWith('ambassador'));
    if (all.every((c) => !has(c, 'to'))) lines.push({ tone: 'danger', text: 'Quitó Embajador' });
    else if (active && has(active, 'to')) lines.push({ tone: active.to ? 'success' : 'danger', text: active.to ? 'Activó Embajador' : 'Desactivó Embajador' });
    else lines.push({ tone: 'default', text: 'Cambió datos de Embajador' });
  }
  if (tags.includes('account')) {
    const email = fields.get('email');
    const tiktok = fields.get('tiktokUsername') || fields.get('tiktokLinkedUsername');
    if (email) lines.push({ tone: 'warning', text: 'Cambió el correo', sensitive: 'email', from: email.from, to: email.to });
    if (tiktok) lines.push({ tone: 'warning', text: 'Cambió el @ de TikTok', sensitive: 'handle', from: tiktok.from, to: tiktok.to });
    if (!email && !tiktok) lines.push({ tone: 'default', text: 'Cambió datos de la cuenta' });
  }
  if (tags.includes('billing')) lines.push({ tone: 'default', text: 'Tocó datos de cobro (Stripe)' });
  if (!lines.length) {
    lines.push({ tone: 'default', text: changes.length === 1 ? `Cambió ${changes[0].field}` : `Cambió ${changes.length} campos` });
  }
  return lines;
}

/** Parámetros del buscador → query string de /api/backoffice/audit. */
export function auditQueryString({ uid, q, tag, from, to, before, limit } = {}) {
  const params = new URLSearchParams();
  if (uid) params.set('uid', uid);
  if (q && q.trim()) params.set('q', q.trim());
  if (tag && tag !== 'all') params.set('tag', tag);
  // Días del selector de fecha (hora local): desde las 00:00 hasta las 23:59:59.
  const day = (value, end) => {
    const d = new Date(`${value}T${end ? '23:59:59.999' : '00:00:00.000'}`);
    return Number.isFinite(d.getTime()) ? String(d.getTime()) : '';
  };
  if (from && day(from, false)) params.set('from', day(from, false));
  if (to && day(to, true)) params.set('to', day(to, true));
  if (before) params.set('before', String(before));
  if (limit) params.set('limit', String(limit));
  const text = params.toString();
  return text ? `?${text}` : '';
}
